import SEO from "../../components/SEO";
import {
  BsArrowRight,
  BsBell,
  BsBoxSeam,
  BsBuildings,
  BsCalculator,
  BsCashCoin,
  BsCreditCard,
  BsGeoAlt,
  BsLightningCharge,
  BsPinMap,
  BsShieldCheck,
  BsTree,
  BsTruck,
} from "react-icons/bs";

function Step({ number, icon, title, children, isLast }) {
  return (
    <li className="relative flex gap-5 md:gap-6">
      <div className="flex flex-col items-center">
        <span className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-lg font-semibold text-white shadow-md">
          {number}
        </span>
        {!isLast && <span className="w-0.5 flex-1 bg-purple-200 my-2" aria-hidden="true" />}
      </div>
      <div className={`flex-1 bg-white rounded-2xl border border-purple-100 shadow-sm p-5 md:p-6 ${isLast ? "" : "mb-6"}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-purple-600 text-2xl" aria-hidden="true">{icon}</span>
          <h3 className="text-xl text-gray-900">{title}</h3>
        </div>
        <div className="text-gray-600 leading-relaxed space-y-2">{children}</div>
      </div>
    </li>
  );
}

function DeliveryOption({ icon, title, area, points }) {
  return (
    <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6 flex flex-col">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-2xl mb-4" aria-hidden="true">
        {icon}
      </span>
      <h3 className="text-xl text-gray-900">{title}</h3>
      <p className="text-sm font-medium text-purple-700 mt-1 mb-4">{area}</p>
      <ul className="space-y-2 text-gray-600">
        {points.map((point) => (
          <li key={point} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-purple-400 flex-shrink-0" aria-hidden="true" />
            {point}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const HowItWorksPage = () => {
  const reasons = [
    { icon: <BsCashCoin />, title: "Affordable, upfront pricing", text: "See the price before you book. Referral wallet credit is applied automatically when you pay." },
    { icon: <BsLightningCharge />, title: "Same-day and next-day", text: "Choose the delivery speed that fits your budget when you get your quote." },
    { icon: <BsTree />, title: "Eco-friendly", text: "We use vehicles already travelling your route instead of adding more cars to the road." },
    { icon: <BsShieldCheck />, title: "Insured and handled with care", text: "Your parcel is collected and delivered by verified drivers." },
  ];

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-cyan-100 to-fuchsia-200">
      <SEO
        title="How It Works"
        description="Send a parcel with Parcelin in 5 steps: get an instant quote, book, pay securely, we collect, and you track it to delivery across Gauteng and Limpopo."
        path="/how-it-works"
      />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-5 pt-14 md:pt-20 pb-10 text-center">
        <p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">How it works</p>
        <h1 className="text-4xl md:text-5xl text-purple-800 mb-5">Send a parcel in five steps</h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-8">
          From quote to doorstep, here's what happens when you send with Parcelin.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href="/" className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-full transition-colors">
            Get a quote <BsArrowRight aria-hidden="true" />
          </a>
          <a href="/track-parcel" className="inline-flex items-center gap-2 bg-white hover:bg-purple-50 text-purple-700 font-semibold px-6 py-3 rounded-full border border-purple-200 transition-colors">
            Track a parcel
          </a>
        </div>
      </section>

      {/* Steps */}
      <section aria-label="Steps" className="max-w-3xl mx-auto px-4 sm:px-5 pb-16">
        <ol>
          <Step number={1} icon={<BsCalculator />} title="Get an instant quote">
            <p>Choose where it's going from and to, pick a parcel size and see the price straight away. No sign-up needed.</p>
          </Step>
          <Step number={2} icon={<BsBoxSeam />} title="Add the details and book">
            <p>
              Pick a collection date and delivery speed, add the pickup address and the receiver's name and phone number. You'll
              log in or sign up with your cellphone number to book.
            </p>
          </Step>
          <Step number={3} icon={<BsCreditCard />} title="Pay securely online">
            <p>
              Pay through PayFast. Any wallet credit you've earned is taken off first. You'll get a confirmation email with your
              8-digit tracking number.
            </p>
          </Step>
          <Step number={4} icon={<BsTruck />} title="We collect your parcel">
            <p>On your collection date, a driver already travelling your route picks up the parcel from your pickup address.</p>
          </Step>
          <Step number={5} icon={<BsBell />} title="Track it to delivery" isLast>
            <p>
              Follow its status on the{" "}
              <a href="/track-parcel" className="text-purple-700 font-semibold underline decoration-purple-300 underline-offset-4 hover:decoration-purple-700">
                tracking page
              </a>
              . The receiver gets a WhatsApp message (or SMS) when it's out for delivery and again when it's delivered.
            </p>
          </Step>
        </ol>
      </section>

      {/* Delivery options */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-5 py-14 md:py-16">
          <div className="max-w-3xl mb-10">
            <p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Two ways to send</p>
            <h2 className="text-3xl md:text-4xl text-gray-900">Short distance or long distance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <DeliveryOption
              icon={<BsPinMap />}
              title="Short distance"
              area="Within 70km of our Midrand warehouse"
              points={["Door-to-door: we collect from one address and deliver to another", "Great for deliveries across Gauteng"]}
            />
            <DeliveryOption
              icon={<BsGeoAlt />}
              title="Long distance"
              area="Gauteng ↔ Limpopo"
              points={[
                "Polokwane, Giyani, Malamulele, Tzaneen, Bungeni, Thohoyandou and Phalaborwa",
                "Collected from your address and delivered to a drop-off address or collection point",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Why Parcelin */}
      <section className="max-w-6xl mx-auto px-4 sm:px-5 py-14 md:py-16">
        <div className="max-w-3xl mb-10">
          <p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Why Parcelin</p>
          <h2 className="text-3xl md:text-4xl text-gray-900">Simple, affordable and greener</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reasons.map((reason) => (
            <div key={reason.title} className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-xl mb-4" aria-hidden="true">
                {reason.icon}
              </span>
              <h3 className="text-lg text-gray-900 mb-2">{reason.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{reason.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 bg-white rounded-2xl border border-purple-100 shadow-sm p-6">
          <BsBuildings className="text-purple-600 text-2xl flex-shrink-0" aria-hidden="true" />
          <p className="flex-1 text-gray-600">
            <span className="font-semibold text-gray-900">Sending for a business?</span> Get a business dashboard, bulk uploads, invoicing
            options and Shopify, WooCommerce and API integrations.
          </p>
          <a href="/about-us" className="inline-flex items-center gap-2 self-start md:self-auto text-sm font-medium text-purple-700 hover:text-purple-900">
            See business features <BsArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-5 pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-purple-700 to-fuchsia-600 text-white p-8 md:p-12 text-center">
          <h2 className="text-3xl md:text-4xl mb-3">Ready to send your parcel?</h2>
          <p className="text-white/85 text-lg mb-7">Get an instant quote in under a minute.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="/" className="inline-flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-50 font-semibold px-7 py-3.5 rounded-full transition-colors">
              Get a quote <BsArrowRight aria-hidden="true" />
            </a>
            <a href="/faq" className="inline-flex items-center gap-2 border border-white/60 hover:bg-white/10 font-semibold px-7 py-3.5 rounded-full transition-colors">
              Read the FAQ
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
